import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Typography,
  Box,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Delete as DeleteIcon,
  InsertDriveFile as FileIcon,
  PictureAsPdf as PdfIcon,
  Description as DocIcon,
  TableChart as ExcelIcon,
  FolderZip as ZipIcon,
} from '@mui/icons-material';
import { LibraryFile } from '../../types/library';

interface FileListTableProps {
  files: LibraryFile[];
  onDownload: (file: LibraryFile) => void;
  onDelete?: (file: LibraryFile) => void;
  isAdmin: boolean;
}

const getFileIcon = (mimeType: string | null) => {
  if (!mimeType) return <FileIcon />;
  if (mimeType.includes('pdf')) return <PdfIcon color="error" />;
  if (mimeType.includes('word') || mimeType.includes('document')) return <DocIcon color="primary" />;
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return <ExcelIcon color="success" />;
  if (mimeType.includes('zip')) return <ZipIcon color="warning" />;
  return <FileIcon />;
};

const formatFileSize = (bytes: number | null): string => {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const FileListTable: React.FC<FileListTableProps> = ({
  files,
  onDownload,
  onDelete,
  isAdmin,
}) => {
  if (files.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <FileIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
        <Typography color="text.secondary">No files in this folder</Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>File</TableCell>
            <TableCell>Size</TableCell>
            <TableCell>Document Date</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {files.map((file) => (
            <TableRow key={file.id} hover sx={{ '& > td': { py: 1.5 } }}>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  {getFileIcon(file.mime_type)}
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body1" fontWeight={600} sx={{ color: 'grey.800', lineHeight: 1.35 }}>
                      {file.title}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.72rem' }}>
                      {file.original_name}
                    </Typography>
                  </Box>
                </Box>
              </TableCell>
              <TableCell>
                <Typography variant="body2">{formatFileSize(file.file_size)}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {file.change_date ? new Date(file.change_date).toLocaleDateString() : '-'}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                  <Tooltip title="Download">
                    <IconButton size="small" onClick={() => onDownload(file)} color="primary">
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {isAdmin && (
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => onDelete?.(file)} color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default FileListTable;
